import {useCallback, useRef, useState} from 'react';

// Leaving full height only well below the enter point keeps a popup sized near the limit from flip-flopping.
const HYSTERESIS = 0.06;

/**
 * Tracks whether a popup's natural height reaches `ratio` of the viewport. Measures the intrinsic
 * content height (`body.scrollHeight`, invariant to the current mode) plus the fixed chrome, so the
 * result cannot oscillate when toggling full height changes the available space. Both returned refs
 * are callback refs so the observers re-attach when the dialog swaps its DOM node on open.
 */
const useExceedsViewportHeight = (
    ratio: number
): readonly [(node: HTMLDivElement | null) => void, (node: HTMLDivElement | null) => void, boolean] => {
    const [isFull, setIsFull] = useState(false);
    const popupRef = useRef<HTMLDivElement | null>(null);
    const bodyRef = useRef<HTMLDivElement | null>(null);
    const cleanupRef = useRef<(() => void) | null>(null);
    const frameRef = useRef<number | null>(null);

    const attach = useCallback(() => {
        cleanupRef.current?.();
        cleanupRef.current = null;

        const popup = popupRef.current;
        const body = bodyRef.current;
        if (!popup || !body) return;

        // Hysteresis must not leak across popups: the first measurement of this mount uses the plain enter point.
        let isFirstMeasure = true;
        const measure = () => {
            frameRef.current = null;
            // Build the height top-down from the content so full height's empty space below the body never inflates it.
            const naturalRatio = (body.offsetTop + body.scrollHeight) / window.innerHeight;
            setIsFull((wasFull) => naturalRatio >= (wasFull && !isFirstMeasure ? ratio - HYSTERESIS : ratio));
            isFirstMeasure = false;
        };
        const schedule = () => {
            if (frameRef.current) cancelAnimationFrame(frameRef.current);
            frameRef.current = requestAnimationFrame(measure);
        };

        const resizeObserver = new ResizeObserver(schedule);
        const mutationObserver = new MutationObserver(schedule);

        schedule();
        resizeObserver.observe(popup);
        resizeObserver.observe(body);
        mutationObserver.observe(body, {childList: true, subtree: true, characterData: true});
        window.addEventListener('resize', schedule);

        cleanupRef.current = () => {
            if (frameRef.current) cancelAnimationFrame(frameRef.current);
            resizeObserver.disconnect();
            mutationObserver.disconnect();
            window.removeEventListener('resize', schedule);
        };
    }, [ratio]);

    const popupCallbackRef = useCallback(
        (node: HTMLDivElement | null) => {
            popupRef.current = node;
            attach();
        },
        [attach]
    );
    const bodyCallbackRef = useCallback(
        (node: HTMLDivElement | null) => {
            bodyRef.current = node;
            attach();
        },
        [attach]
    );

    return [popupCallbackRef, bodyCallbackRef, isFull];
};

export default useExceedsViewportHeight;
